// Import React elements
import React from "react";

// Import hooks
import { useMMMOptions } from "../../hooks/useMMMOptions";




// This helper finds a link in the array that matches the given MMM ids
export const FindCustomProductLinkByMMM = (links, materialIdRaw, methodIdRaw, modeIdRaw) => {

    // Convert the incoming ids to trimmed strings so numbers and strings match
    const materialKey = String(materialIdRaw ?? "").trim();
    const methodKey   = String(methodIdRaw ?? "").trim();
    const modeKey     = String(modeIdRaw ?? "").trim();

    // If any of the three ids are missing, we cannot find a full match
    if (!materialKey || !methodKey || !modeKey) {

        // Return null to indicate no matching link
        return null;

    }

    // Look through the links array for a record where all three ids match
    const link = (links || []).find((item) => {

        // Normalise each link's ids to trimmed strings
        const itemMaterialKey = String(item?.material_id ?? "").trim();
        const itemMethodKey   = String(item?.method_id ?? "").trim();
        const itemModeKey     = String(item?.mode_id ?? "").trim();

        // Return true only when all three ids match
        return (
            itemMaterialKey === materialKey &&
            itemMethodKey === methodKey &&
            itemModeKey === modeKey
        );

    }) || null;

    // Return the matching link or null if not found
    return link;

};



// Helper function to find the exact link for the selected triple
const findLinkForSelection = (links, materialId, methodId, modeId) => {

    // Normalize all selected ids to strings
    const materialKey = String(materialId ?? "");
    const methodKey = String(methodId ?? "");
    const modeKey = String(modeId ?? "");

    // Return the first link that matches all three ids
    return (links || []).find((link) => {

        // Normalize this link's ids to strings
        const linkMaterialKey = String(link?.material_id ?? "");
        const linkMethodKey = String(link?.method_id ?? "");
        const linkModeKey = String(link?.mode_id ?? "");

        // Check all three ids match
        return (
            linkMaterialKey === materialKey &&
            linkMethodKey === methodKey &&
            linkModeKey === modeKey
        );

    }) || null;

};



// Export the reusable selector component
export const CustomProductLinkSelector = ({

    // The array of link records from API
    links = [], 

    // The current selection { materialId, methodId, modeId }
    value = {},

    // Callback when the selection changes
    onChange

}) => {


    // Safely read the current selected ids from value
    const materialId = value.materialId || "";
    const methodId = value.methodId || "";
    const modeId = value.modeId || "";

    // Use the custom hook to get filtered dropdown options
    const { materialOptions, methodOptions, modeOptions } = useMMMOptions(
        links,
        materialId,
        methodId
    );


    // Handler for when material dropdown changes
    const handleMaterialChange = (event) => {

        // Read the selected value from the event (always a string from <select>)
        const nextMaterialId = event.target.value;

        // Build the next selection: when material changes, reset method and mode
        const nextSelection = {

            // New material id
            materialId: nextMaterialId,

            // Clear the method selection
            methodId: "",

            // Clear the mode selection
            modeId: ""

        };

        // If a callback is provided, call it with the new selection and matching link (if any)
        if (typeof onChange === "function") {

            // Compute the matching link (will be null because method and mode are blank)
            const link = findLinkForSelection(
                links,
                nextSelection.materialId,
                nextSelection.methodId,
                nextSelection.modeId
            );

            // Call the parent with the combined data
            onChange({ ...nextSelection, link });

        }

    };



    // Handler for when method dropdown changes
    const handleMethodChange = (event) => {

        // Read the selected method id as a string
        const nextMethodId = event.target.value;

        // Build the next selection: keep material, reset mode
        const nextSelection = {

            // Keep the existing material
            materialId: materialId,

            // Set the new method
            methodId: nextMethodId,

            // Clear the mode selection
            modeId: ""

        };

        // If a callback is provided, call it
        if (typeof onChange === "function") {

            // Compute the matching link (still no full triple yet because mode is blank)
            const link = findLinkForSelection(
                links,
                nextSelection.materialId,
                nextSelection.methodId,
                nextSelection.modeId
            );

            // Call parent with new selection
            onChange({ ...nextSelection, link });

        }

    };



    // Handler for when mode dropdown changes
    const handleModeChange = (event) => {

        // Read the selected mode id as a string
        const nextModeId = event.target.value;

        // Build the next selection: keep material and method, set mode
        const nextSelection = {

            // Existing material
            materialId: materialId,

            // Existing method
            methodId: methodId,

            // New mode
            modeId: nextModeId

        };

        // If a callback is provided, call it
        if (typeof onChange === "function") {

            // Now we likely have a full triple, so we can compute the exact link
            const link = findLinkForSelection(
                links,
                nextSelection.materialId,
                nextSelection.methodId,
                nextSelection.modeId
            );

            // Call parent with selection and resolved link
            onChange({ ...nextSelection, link });

        }

    };



    // Render the three dropdowns
    return (

        // Outer container with a BEM block class
        <div className="custom-product-link-selector">

            {/* Material field */}
            <div className="custom-product-link-selector__field">

                {/* Label for materials */}
                <label className="custom-product-link-selector__label" htmlFor="material-select">
                    Material
                </label>

                {/* Material dropdown */}
                <select
                    id="material-select"
                    className="custom-product-link-selector__select"
                    value={materialId}
                    onChange={handleMaterialChange}
                >

                    {/* Placeholder option */}
                    <option value="">
                        Select a material...
                    </option>

                    {/* Map over material options */}
                    {materialOptions.map((option) => (

                        <option
                            key={String(option.id)}
                            value={String(option.id)}
                        >
                            {option.name}
                        </option>

                    ))}

                </select>

            </div>

            {/* Method field */}
            <div className="custom-product-link-selector__field">

                {/* Label for methods */}
                <label className="custom-product-link-selector__label" htmlFor="method-select">
                    Method
                </label>

                {/* Method dropdown (disabled until a material is selected) */}
                <select
                    id="method-select"
                    className="custom-product-link-selector__select"
                    value={methodId}
                    onChange={handleMethodChange}
                    disabled={!materialId}
                >

                    {/* Placeholder option */}
                    <option value="">
                        {materialId ? "Select a method..." : "Select a material first"}
                    </option>

                    {/* Map over method options */}
                    {methodOptions.map((option) => (

                        <option
                            key={String(option.id)}
                            value={String(option.id)}
                        >
                            {option.name}
                        </option>

                    ))}

                </select>

            </div>

            {/* Mode field */}
            <div className="custom-product-link-selector__field">

                {/* Label for modes */}
                <label className="custom-product-link-selector__label" htmlFor="mode-select">
                    Mode
                </label>

                {/* Mode dropdown (disabled until a method is selected) */}
                <select
                    id="mode-select"
                    className="custom-product-link-selector__select"
                    value={modeId}
                    onChange={handleModeChange}
                    disabled={!materialId || !methodId}
                >

                    {/* Placeholder option */}
                    <option value="">
                        {methodId ? "Select a mode..." : "Select a material and method first"}
                    </option>

                    {/* Map over mode options */}
                    {modeOptions.map((option) => (

                        <option
                            key={String(option.id)}
                            value={String(option.id)}
                        >
                            {option.name}
                        </option>

                    ))}

                </select>

            </div>

        </div>

    );

};
